// src/services/draft-reading-practice-service.js

import { api, extractResponseData } from "./base";

// Constants
const ACTIVITY_TYPES = ["DECODE", "VOCABULARY", "FLUENCY", "COMPREHENSION"];

export const createReadingPractice = async ({
  gradeLevel,
  categoryId,
  topic,
  wordsPerParagraph,
  paragraphCount,
  paragraphContentStyle,
}) => {
  const data = {
    gradeLevel,
    categoryId,
    topic,
    wordsPerParagraph,
    numberOfParagraphs: paragraphCount,
    paragraphContentStyle,
  };

  try {
    const response = await api.post(
      "/platform/requests/reading-practices",
      data,
      { timeout: 30000 },
    );
    return extractResponseData(response);
  } catch (error) {
    console.error("Error in request submission:", error);
    throw new Error(`Create failed: ${error.message}`);
  }
};

const GRADE_MAPPING = {
  1: "ONE",
  2: "TWO",
  3: "THREE",
  4: "FOUR",
  5: "FIVE",
  6: "SIX",
  7: "SEVEN",
  8: "EIGHT",
  9: "NINE",
  10: "TEN",
  11: "ELEVEN",
  12: "TWELVE",
};

export const search = async ({
  term,
  categoryIds,
  gradeLevels,
  pageNumber,
  pageSize,
  statuses,
}) => {
  try {
    const mappedGradeLevels = gradeLevels
      ? gradeLevels.map((g) => GRADE_MAPPING[g] || g)
      : undefined;
    const response = await api.post("/platform/reading-practices/search", {
      term,
      categoryIds,
      gradeLevels: mappedGradeLevels,
      pageNumber,
      pageSize,
      statuses,
    });

    if (!response?.data) {
      throw new Error("Invalid response structure");
    }

    const reposne = extractResponseData(response);

    return reposne;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || error.message || "Unknown error";
    console.error(`Error fetching covers: ${errorMessage}`);
    throw new Error(`Failed to fetch covers: ${errorMessage}`);
  }
};

export const get = async (id) => {
  try {
    const response = await api.get(`/platform/reading-practices/${id}`); // Todo - replace with reading-practices get
    return extractResponseData(response);
  } catch (error) {
    console.error(`Error fetching content with ID ${id}:`, error);
    throw new Error(`Failed to fetch content with ID ${id}: ${error.message}`);
  }
};

export const publish = async (id) => {
  try {
    const response = await api.post(
      `/platform/reading-practices/${id}/publish`,
    );
    return extractResponseData(response);
  } catch (error) {
    console.error(`Error publishing practice with ID ${id}:`, error);
    throw new Error(
      `Failed to publish practice with ID ${id}: ${error.message}`,
    );
  }
};

export const approve = async (id) => {
  try {
    const response = await api.post(
      `/platform/reading-practices/${id}/approve`,
    );
    return extractResponseData(response);
  } catch (error) {
    console.error(`Error approving practice with ID ${id}:`, error);
    throw new Error(
      `Failed to approve practice with ID ${id}: ${error.message}`,
    );
  }
};

export const release = async (id) => {
  try {
    const response = await api.post(
      `/platform/reading-practices/${id}/release`,
    );
    return extractResponseData(response);
  } catch (error) {
    console.error(`Error releasing practice with ID ${id}:`, error);
    throw new Error(
      `Failed to release practice with ID ${id}: ${error.message}`,
    );
  }
};

export const archive = async (id) => {
  try {
    const response = await api.post(
      `/platform/reading-practices/${id}/archive`,
    );
    return extractResponseData(response);
  } catch (error) {
    console.error(`Error archiving practice with ID ${id}:`, error);
    throw new Error(
      `Failed to archive practice with ID ${id}: ${error.message}`,
    );
  }
};

export const recreateCover = async (id) => {
  try {
    const response = await api.post(
      `/platform/reading-practices/${id}/recreate-cover`,
      null,
      { timeout: 300000 },
    );
    return extractResponseData(response);
  } catch (error) {
    console.error(`Error recreating cover for practice ID ${id}:`, error);
    throw new Error(`Recreate failed: ${error.message}`);
  }
};

export const update = async (id, data) => {
  try {
    const title = data.cover?.title?.raw?.value || "";
    const topic = data.cover?.topic?.raw?.value || "";
    const synopsis = data.cover?.synopsis?.raw?.value || "";
    const thumbnailUrl = data.cover?.thumbnailImage?.url || "";

    const apiData = {
      cover: {
        title,
        gradeLevel: data.gradeLevel || 1,
        topic,
        synopsis,
        thumbnailUrl,
      },
      activities: [],
    };

    ACTIVITY_TYPES.forEach((type) => {
      const activity = data.activities?.find((a) => a.type === type);
      if (activity) {
        const builtActivity = buildActivity(type, activity.content);
        if (builtActivity) {
          apiData.activities.push(builtActivity);
        }
      }
    });

    const response = await api.put(
      `/platform/reading-practices/${id}`,
      apiData,
    );
    return extractResponseData(response);
  } catch (error) {
    console.error("Error updating content:", error);
    throw new Error(`Update failed: ${error.message}`);
  }
};

const safeMap = (arr, mapper) => (Array.isArray(arr) ? arr.map(mapper) : []);

const buildActivity = (type, content = {}) => {
  switch (type) {
    case "DECODE":
      return {
        type,
        content: {
          words: safeMap(content.words, (w) => w.raw?.value || ""),
        },
      };
    case "VOCABULARY":
      return {
        type,
        content: {
          sentences: safeMap(content.sentences, (s) => s.raw?.value || ""),
        },
      };
    case "FLUENCY":
      return {
        type,
        content: {
          paragraphs: safeMap(content.paragraphs, (p) => p.raw?.value || ""),
        },
      };
    case "COMPREHENSION":
      return {
        type,
        content: {
          questions: safeMap(content.questions, (q) => ({
            text: q.questionText?.raw?.value || "",
            options: safeMap(q.options, (o) => o.raw?.value || ""),
            correctIndex: q.correctIndex || 0,
          })),
        },
      };
    default:
      return null;
  }
};
